import Lottie from 'lottie-react';
import React, { createContext, useContext, useState, useRef } from 'react';
import ReactDOM from 'react-dom';

// Ensure these paths point to your actual Lottie JSON files
import SuccessAnimation from "assets/images/SuccessAnimation.json";
import ErrorAnimation from "assets/images/ErrorAnimation.json";
import WarningAnimation from "assets/images/WarningAnimation.json";

const ModalContext = createContext();
let externalShowModal = null;

export const useModal = () => useContext(ModalContext);

export const ModalProvider = ({ children }) => {
  const [modal, setModal] = useState({
    visible: false,
    title: "",
    content: null,
    info: "success",
    showConfirm: false,
    onConfirm: null,
    onCancel: null,
    confimLabel: "",
    cancelLabel: "",
  });

  const timeoutRef = useRef(null);

  const showModal = ({ 
    content, 
    title = "", 
    info = "success", 
    showConfirm = false, 
    onConfirm = null, 
    onCancel = null, 
    confimLabel = "", 
    cancelLabel = "" 
  }) => {
    setModal({ 
      visible: true, 
      title, 
      content, 
      info, 
      showConfirm, 
      onConfirm, 
      onCancel, 
      confimLabel, 
      cancelLabel 
    });

    // Auto-close only if confirmation buttons are NOT shown
    if (!showConfirm) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        closeModal();
      }, 3000);
    }
  };

  const closeModal = () => {
    clearTimeout(timeoutRef.current);
    setModal({
      visible: false,
      title: "",
      content: null,
      info: "success",
      showConfirm: false,
      onConfirm: null,
      onCancel: null,
      confimLabel: "",
      cancelLabel: "",
    });
  };

  externalShowModal = showModal;

  const handleConfirm = () => {
    modal.onConfirm?.();
    closeModal();
  };

  const handleCancel = () => {
    modal.onCancel?.();
    closeModal();
  };

  const renderAnimation = (info) => {
    switch (info) {
      case "success":
        return <Lottie animationData={SuccessAnimation} loop={false} className='h-[10vh]' />;
      case "error":
        return <Lottie animationData={ErrorAnimation} loop={false} className='h-[10vh]' />;
      case "warning":
        return <Lottie animationData={WarningAnimation} loop={false} className='h-[10vh]' />;
      default:
        return null;
    }
  };

  return (
    <ModalContext.Provider value={{ showModal }}>
      {children}
      {modal.visible &&
        ReactDOM.createPortal(
          <div
            style={modalStyles.overlay}
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-[#00000033] backdrop-blur-[2px]"
          >
            <div className="w-[32vw] bg-[#fff] rounded-[0.75vw] p-[1.5vw] relative shadow-[0_4px_20px_rgba(0,0,0,0.1)] outline-none font-sans m-auto">

              <button
                onClick={handleCancel}
                className="absolute top-[0.75vw] right-[0.75vw] text-[#9CA3AF] hover:text-[#4B5563] transition-colors"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className="w-[1.1vw] h-[1.1vw]" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>

              <div className="flex flex-col items-center justify-center gap-[0.5vh] mb-[1.5vh]">
                {["success", "error", "warning"].includes(modal.info) ? (
                  <div className="h-[7vh]">{renderAnimation(modal.info)}</div>
                ) : (
                  <div className="w-[2.2vw] h-[2.2vw] bg-[#F0F5FF] rounded-[0.5vw] flex items-center justify-center text-[#3B4DF2] shrink-0">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className="w-[1.1vw] h-[1.1vw]" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="10" width="5" height="5" rx="1" />
                      <rect x="16" y="4" width="5" height="5" rx="1" />
                      <rect x="16" y="15" width="5" height="5" rx="1" />
                      <path d="M8 12.5h3.5" />
                      <path d="M11.5 6.5v11" />
                      <path d="M11.5 6.5h4.5" />
                      <path d="M11.5 17.5h4.5" />
                    </svg>
                  </div>
                )}
                {modal.title && (
                  <h2 className="text-[#374AF2] text-[1.1vw] font-montserrat font-[600] tracking-tight m-0 text-center">
                    {modal.title}
                  </h2>
                )}
              </div>

              {modal.content && (
                <div className="mb-[1.25vw] text-center font-inter font-[400]">
                  <p className="text-[0.9vw] text-[#000] m-0 leading-snug">
                    {modal.content}
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              {modal.showConfirm && (
                <div className="flex items-center gap-[0.75vw] w-full mt-[0.5vw]">
                  {!!modal.onCancel && (
                    <button
                      type="button"
                      onClick={handleCancel}
                      className="flex-1 py-[0.6vw] text-[0.85vw] font-inter font-[500] text-[#000] bg-white border border-[#D1D5DB] rounded-[0.4vw] hover:bg-gray-50 transition-colors focus:outline-none"
                    >
                      {modal.cancelLabel || "Cancel"}
                    </button>
                  )}
                  {!!modal.onConfirm && (
                    <button
                      onClick={handleConfirm}
                      className="flex-1 py-[0.6vw] text-[0.85vw] font-inter font-[500] text-[#fff] bg-[#374AF2] rounded-[0.4vw] hover:bg-[#3142d9] transition-colors focus:outline-none"
                    >
                      {modal.confimLabel || "Confirm"}
                    </button>
                  )}
                </div>
              )}

            </div>
          </div>,
          document.getElementById('root')
        )}
    </ModalContext.Provider>
  );
};

// Exported function to call the modal from anywhere
export const showGlobalModal = (content, info = "success", options = {}) => {
  if (externalShowModal) {
    externalShowModal({ content, info, ...options });
  } else {
    console.warn('Modal system not ready');
  }
};

const modalStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
  },
};